import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  OAUTH_COMPLETED_MESSAGE_TYPE,
  INSTAGRAM_POPUP_CLOSE_DELAY_MS,
  OAUTH_INSTAGRAM_AUTH_RETRY_ATTEMPTS,
  OAUTH_INSTAGRAM_AUTH_RETRY_DELAY_MS,
  OAUTH_INSTAGRAM_CALLBACK_TIMEOUT_MS,
  OAUTH_INSTAGRAM_POPUP_CLOSED_GRACE_MS,
  OAUTH_INSTAGRAM_SYNC_ATTEMPTS,
  OAUTH_INSTAGRAM_SYNC_DELAY_MS,
  OAUTH_POLL_INTERVAL_MS,
  buildInstagramOAuthUrl,
  clearInstagramOAuthPending,
  clearStoredOAuthResult,
  consumeInstagramOAuthReturnResult,
  markInstagramOAuthPending,
  openInstagramOAuthPopup,
  peekInstagramOAuthReturnResult,
  readStoredOAuthResult,
  relaunchInstagramOAuthPopup,
  shouldUseInstagramOAuthRedirect,
} from '../../../shared/campaign/utils/instagramOAuth';
import { launchInstagramOAuthRedirect } from '../../../shared/campaign/utils/instagramOAuthRedirect';
import {
  notifyErrorCustom,
  notifyWarningCustom,
} from '../../../utils/notify';

import { persistActiveCampaignForReturn } from '../utils/campaignRestore';

const CHAT_SOCKET_RETURN_PAGE = 'ChatSocketPage';

/** Always return to /ChatSocket (never /) so PublicLayout does not strip OAuth state. */
export const getChatSocketOAuthReturnPath = (search = '') => {
  const query =
    search || (typeof window !== 'undefined' ? window.location.search : '');
  return `/ChatSocket${query || ''}`;
};

const normalizeChatSocketPath = (path = '') => {
  const queryIndex = path.indexOf('?');
  const pathname = queryIndex >= 0 ? path.slice(0, queryIndex) : path;
  const search = queryIndex >= 0 ? path.slice(queryIndex) : '';
  const normalizedPathname =
    pathname === '/' || pathname.toLowerCase() === '/chatsocket'
      ? '/ChatSocket'
      : pathname;

  return `${normalizedPathname}${search}`;
};

const chatSocketPathsMatch = (expectedPath, currentPath) => {
  if (!expectedPath) {
    return true;
  }

  return (
    normalizeChatSocketPath(expectedPath) === normalizeChatSocketPath(currentPath)
  );
};

const useChatSocketInstagramOAuthReturn = ({
  onSuccess,
  onFailure,
  enabled = true,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef(false);
  const callbacksRef = useRef({ onSuccess, onFailure });

  callbacksRef.current = { onSuccess, onFailure };

  useEffect(() => {
    if (!enabled || handledRef.current) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}`;
    const stateResult = location.state?.instagramOAuth;
    const storedResult = stateResult ? null : peekInstagramOAuthReturnResult();
    const result = stateResult || storedResult;

    if (!result) {
      return;
    }

    if (!chatSocketPathsMatch(result.returnPath, currentPath)) {
      return;
    }

    handledRef.current = true;
    consumeInstagramOAuthReturnResult();

    if (location.state?.instagramOAuth) {
      navigate(getChatSocketOAuthReturnPath(location.search), {
        replace: true,
        state: null,
      });
    }

    if (result.success) {
      callbacksRef.current.onSuccess?.(result);
      return;
    }

    callbacksRef.current.onFailure?.(result);
  }, [enabled, location.pathname, location.search, location.state, navigate]);
};

export const useChatSocketInstagramOAuth = ({
  businessId,
  campaignArtGenerationId,
  instagramNetwork,
  loadConnections,
  setBusinessId,
  setSelectedNetworks,
  openPostAuthPopover,
  setIsConnecting,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const oauthIntervalRef = useRef(null);
  const oauthStoragePollRef = useRef(null);
  const oauthSessionTimeoutRef = useRef(null);
  const oauthWindowRef = useRef(null);
  const oauthPopupFallbackCloseRef = useRef(null);
  const oauthHandledRef = useRef(false);
  const oauthIncompleteNotifiedRef = useRef(false);
  const oauthHasReachedRedirectRef = useRef(false);
  const oauthPostMessageReceivedRef = useRef(false);
  const oauthRetryCountRef = useRef(0);
  const watchInstagramOAuthWindowRef = useRef(null);
  const restartInstagramOAuthAttemptRef = useRef(null);
  const handleInstagramOAuthSuccessRef = useRef(null);

  const clearOAuthSessionTimeout = useCallback(() => {
    if (oauthSessionTimeoutRef.current) {
      clearTimeout(oauthSessionTimeoutRef.current);
      oauthSessionTimeoutRef.current = null;
    }
  }, []);

  const clearInstagramOAuthWatchers = useCallback(() => {
    if (oauthIntervalRef.current) {
      clearInterval(oauthIntervalRef.current);
      oauthIntervalRef.current = null;
    }

    if (oauthStoragePollRef.current) {
      clearInterval(oauthStoragePollRef.current);
      oauthStoragePollRef.current = null;
    }

    if (oauthPopupFallbackCloseRef.current) {
      clearTimeout(oauthPopupFallbackCloseRef.current);
      oauthPopupFallbackCloseRef.current = null;
    }

    clearOAuthSessionTimeout();
  }, [clearOAuthSessionTimeout]);

  const resetOAuthAttemptState = useCallback(() => {
    oauthHandledRef.current = false;
    oauthIncompleteNotifiedRef.current = false;
    oauthHasReachedRedirectRef.current = false;
    oauthPostMessageReceivedRef.current = false;
    clearStoredOAuthResult();
  }, []);

  const syncInstagramConnectionWithRetry = useCallback(
    async (targetBusinessId) => {
      if (!instagramNetwork || !targetBusinessId) {
        return false;
      }

      for (let attempt = 0; attempt < OAUTH_INSTAGRAM_SYNC_ATTEMPTS; attempt += 1) {
        const latestConnections = await loadConnections(targetBusinessId, {
          silent: true,
        });
        const connected = !!latestConnections?.[instagramNetwork.key];

        if (connected) {
          setSelectedNetworks((previous) =>
            previous.includes(instagramNetwork.id)
              ? previous
              : [...previous, instagramNetwork.id],
          );
          return true;
        }

        if (attempt < OAUTH_INSTAGRAM_SYNC_ATTEMPTS - 1) {
          await new Promise((resolve) => {
            setTimeout(resolve, OAUTH_INSTAGRAM_SYNC_DELAY_MS);
          });
        }
      }

      return false;
    },
    [instagramNetwork, loadConnections, setSelectedNetworks],
  );

  const schedulePopupFallbackClose = useCallback(() => {
    if (oauthPopupFallbackCloseRef.current) {
      clearTimeout(oauthPopupFallbackCloseRef.current);
    }

    oauthPopupFallbackCloseRef.current = setTimeout(() => {
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
        try {
          oauthWindowRef.current.close();
        } catch {
          // noop
        }
      }
      oauthWindowRef.current = null;
      oauthPopupFallbackCloseRef.current = null;
    }, INSTAGRAM_POPUP_CLOSE_DELAY_MS + 500);
  }, []);

  const hasOAuthCompletionSignal = useCallback(() => {
    if (
      oauthHandledRef.current ||
      oauthPostMessageReceivedRef.current ||
      oauthHasReachedRedirectRef.current
    ) {
      return true;
    }

    return !!readStoredOAuthResult('instagram');
  }, []);

  const finalizeInstagramOAuthFailure = useCallback(
    (messageKey) => {
      oauthHandledRef.current = true;
      clearInstagramOAuthWatchers();
      clearInstagramOAuthPending();
      clearStoredOAuthResult();
      oauthWindowRef.current = null;
      oauthRetryCountRef.current = 0;
      setIsConnecting(false);
      notifyWarningCustom(t(messageKey));
    },
    [clearInstagramOAuthWatchers, setIsConnecting, t],
  );

  const handleInstagramOAuthSuccess = useCallback(
    async (oauthBusinessId) => {
      const targetBusinessId = oauthBusinessId || businessId;

      if (targetBusinessId && targetBusinessId !== businessId) {
        setBusinessId(targetBusinessId);
      }

      const connected = await syncInstagramConnectionWithRetry(targetBusinessId);

      if (connected) {
        oauthRetryCountRef.current = 0;
        setIsConnecting(false);
        await openPostAuthPopover(instagramNetwork);
        return true;
      }

      return false;
    },
    [
      businessId,
      instagramNetwork,
      openPostAuthPopover,
      setBusinessId,
      setIsConnecting,
      syncInstagramConnectionWithRetry,
    ],
  );

  handleInstagramOAuthSuccessRef.current = handleInstagramOAuthSuccess;

  const launchInstagramOAuthRedirectFlow = useCallback(() => {
    persistActiveCampaignForReturn(campaignArtGenerationId);

    return launchInstagramOAuthRedirect({
      businessId,
      returnPage: CHAT_SOCKET_RETURN_PAGE,
      returnPath: getChatSocketOAuthReturnPath(location.search),
    });
  }, [businessId, campaignArtGenerationId, location.search]);

  const waitForOAuthCompletionSignal = useCallback(async () => {
    await new Promise((resolve) => {
      setTimeout(resolve, OAUTH_INSTAGRAM_POPUP_CLOSED_GRACE_MS);
    });

    return hasOAuthCompletionSignal();
  }, [hasOAuthCompletionSignal]);

  const completeInstagramOAuth = useCallback(
    async ({ success = true, closePopup = false, oauthBusinessId } = {}) => {
      if (oauthHandledRef.current || !instagramNetwork) {
        return;
      }

      if (!success) {
        clearInstagramOAuthWatchers();
        clearStoredOAuthResult();

        if (closePopup && oauthWindowRef.current && !oauthWindowRef.current.closed) {
          try {
            oauthWindowRef.current.close();
          } catch {
            // noop
          }
          oauthWindowRef.current = null;
        } else {
          schedulePopupFallbackClose();
        }

        if (restartInstagramOAuthAttemptRef.current?.()) {
          return;
        }

        oauthHandledRef.current = true;
        clearInstagramOAuthPending();
        oauthRetryCountRef.current = 0;
        setIsConnecting(false);
        notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
        return;
      }

      oauthHandledRef.current = true;
      clearInstagramOAuthWatchers();
      clearStoredOAuthResult();
      clearInstagramOAuthPending();

      if (closePopup) {
        if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
          try {
            oauthWindowRef.current.close();
          } catch {
            // noop
          }
        }
        oauthWindowRef.current = null;
      } else {
        schedulePopupFallbackClose();
      }

      const connected = await handleInstagramOAuthSuccess(oauthBusinessId);

      if (connected) {
        return;
      }

      oauthHandledRef.current = false;

      if (restartInstagramOAuthAttemptRef.current?.()) {
        return;
      }

      oauthHandledRef.current = true;
      oauthRetryCountRef.current = 0;
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
    },
    [
      clearInstagramOAuthWatchers,
      handleInstagramOAuthSuccess,
      instagramNetwork,
      schedulePopupFallbackClose,
      setIsConnecting,
      t,
    ],
  );

  const handleInstagramOAuthIncomplete = useCallback(() => {
    if (oauthHandledRef.current) {
      return;
    }

    clearInstagramOAuthWatchers();
    clearStoredOAuthResult();

    if (restartInstagramOAuthAttemptRef.current?.()) {
      return;
    }

    if (oauthIncompleteNotifiedRef.current) {
      return;
    }

    finalizeInstagramOAuthFailure(
      'chatsocket.social_media.errors.instagram_oauth_incomplete',
    );
  }, [clearInstagramOAuthWatchers, finalizeInstagramOAuthFailure]);

  const handlePopupClosedWithoutCallback = useCallback(async () => {
    clearInstagramOAuthWatchers();
    oauthWindowRef.current = null;

    const completedWhileClosing = await waitForOAuthCompletionSignal();
    if (completedWhileClosing) {
      const storedResult = readStoredOAuthResult('instagram');
      if (storedResult && !oauthHandledRef.current) {
        await completeInstagramOAuth({ closePopup: false });
      }
      return;
    }

    handleInstagramOAuthIncomplete();
  }, [
    clearInstagramOAuthWatchers,
    completeInstagramOAuth,
    handleInstagramOAuthIncomplete,
    waitForOAuthCompletionSignal,
  ]);

  const startInstagramStoragePoll = useCallback(() => {
    if (oauthStoragePollRef.current) {
      clearInterval(oauthStoragePollRef.current);
    }

    oauthStoragePollRef.current = setInterval(async () => {
      if (oauthHandledRef.current) {
        clearInstagramOAuthWatchers();
        return;
      }

      const storedResult = readStoredOAuthResult('instagram');
      if (storedResult) {
        await completeInstagramOAuth({ closePopup: false });
      }
    }, OAUTH_POLL_INTERVAL_MS);
  }, [clearInstagramOAuthWatchers, completeInstagramOAuth]);

  const scheduleCallbackTimeout = useCallback(() => {
    clearOAuthSessionTimeout();

    oauthSessionTimeoutRef.current = setTimeout(() => {
      if (oauthHandledRef.current) {
        return;
      }

      handleInstagramOAuthIncomplete();
    }, OAUTH_INSTAGRAM_CALLBACK_TIMEOUT_MS);
  }, [clearOAuthSessionTimeout, handleInstagramOAuthIncomplete]);

  const watchInstagramOAuthWindow = useCallback(
    (oauthWindow, redirectPathMarker) => {
      clearInstagramOAuthWatchers();
      scheduleCallbackTimeout();

      oauthIntervalRef.current = setInterval(async () => {
        if (oauthHandledRef.current) {
          clearInstagramOAuthWatchers();
          return;
        }

        if (oauthWindow.closed) {
          await handlePopupClosedWithoutCallback();
          return;
        }

        try {
          if (oauthWindow.location.href.includes(redirectPathMarker)) {
            oauthHasReachedRedirectRef.current = true;
            clearOAuthSessionTimeout();

            if (oauthIntervalRef.current) {
              clearInterval(oauthIntervalRef.current);
              oauthIntervalRef.current = null;
            }

            startInstagramStoragePoll();
          }
        } catch {
          // Expected while the popup is on instagram.com; keep polling.
        }
      }, OAUTH_POLL_INTERVAL_MS);
    },
    [
      clearInstagramOAuthWatchers,
      clearOAuthSessionTimeout,
      handlePopupClosedWithoutCallback,
      scheduleCallbackTimeout,
      startInstagramStoragePoll,
    ],
  );

  watchInstagramOAuthWindowRef.current = watchInstagramOAuthWindow;

  const startInstagramOAuthPopupFlow = useCallback(() => {
    const authUrl = buildInstagramOAuthUrl(businessId, CHAT_SOCKET_RETURN_PAGE);
    const oauthWindow = openInstagramOAuthPopup(authUrl);

    if (!oauthWindow) {
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
      return null;
    }

    oauthWindowRef.current = oauthWindow;
    watchInstagramOAuthWindow(oauthWindow, 'SocialMediaRedirect');
    return oauthWindow;
  }, [businessId, setIsConnecting, t, watchInstagramOAuthWindow]);

  const restartInstagramOAuthAttempt = useCallback(() => {
    if (!businessId || !instagramNetwork) {
      return false;
    }

    if (oauthRetryCountRef.current >= OAUTH_INSTAGRAM_AUTH_RETRY_ATTEMPTS) {
      return false;
    }

    oauthRetryCountRef.current += 1;
    resetOAuthAttemptState();
    markInstagramOAuthPending(businessId, CHAT_SOCKET_RETURN_PAGE);
    setIsConnecting(true);

    notifyWarningCustom(t('chatsocket.social_media.errors.instagram_oauth_retry'));

    if (shouldUseInstagramOAuthRedirect()) {
      const started = launchInstagramOAuthRedirectFlow();
      if (!started) {
        oauthRetryCountRef.current = 0;
        setIsConnecting(false);
        notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
      }
      return started;
    }

    const runRetry = async () => {
      await new Promise((resolve) => {
        setTimeout(resolve, OAUTH_INSTAGRAM_AUTH_RETRY_DELAY_MS);
      });

      const authUrl = buildInstagramOAuthUrl(businessId, CHAT_SOCKET_RETURN_PAGE);
      const oauthWindow = relaunchInstagramOAuthPopup(oauthWindowRef.current, authUrl);

      if (!oauthWindow) {
        oauthRetryCountRef.current = 0;
        setIsConnecting(false);
        notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
        return;
      }

      oauthWindowRef.current = oauthWindow;
      watchInstagramOAuthWindowRef.current?.(oauthWindow, 'SocialMediaRedirect');
    };

    void runRetry();
    return true;
  }, [
    businessId,
    instagramNetwork,
    launchInstagramOAuthRedirectFlow,
    resetOAuthAttemptState,
    setIsConnecting,
    t,
  ]);

  restartInstagramOAuthAttemptRef.current = restartInstagramOAuthAttempt;

  const cancelInstagramOAuth = useCallback(() => {
    oauthHandledRef.current = true;
    oauthIncompleteNotifiedRef.current = true;
    clearInstagramOAuthWatchers();
    clearInstagramOAuthPending();
    clearStoredOAuthResult();

    if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
      try {
        oauthWindowRef.current.close();
      } catch {
        // noop
      }
    }

    oauthWindowRef.current = null;
    oauthRetryCountRef.current = 0;
    setIsConnecting(false);
  }, [clearInstagramOAuthWatchers, setIsConnecting]);

  const reopenInstagramOAuthWindow = useCallback(() => {
    if (!businessId || !instagramNetwork || shouldUseInstagramOAuthRedirect()) {
      return;
    }

    oauthHandledRef.current = false;
    oauthIncompleteNotifiedRef.current = false;
    resetOAuthAttemptState();
    markInstagramOAuthPending(businessId, CHAT_SOCKET_RETURN_PAGE);
    setIsConnecting(true);
    startInstagramOAuthPopupFlow();
  }, [
    businessId,
    instagramNetwork,
    resetOAuthAttemptState,
    setIsConnecting,
    startInstagramOAuthPopupFlow,
  ]);

  const startInstagramOAuth = useCallback(() => {
    if (!businessId || !instagramNetwork) {
      return null;
    }

    setIsConnecting(true);
    oauthRetryCountRef.current = 0;
    resetOAuthAttemptState();
    markInstagramOAuthPending(businessId, CHAT_SOCKET_RETURN_PAGE);

    if (shouldUseInstagramOAuthRedirect()) {
      const started = launchInstagramOAuthRedirectFlow();
      if (!started) {
        setIsConnecting(false);
        notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
        return false;
      }
      return true;
    }

    persistActiveCampaignForReturn(campaignArtGenerationId);
    return startInstagramOAuthPopupFlow();
  }, [
    businessId,
    campaignArtGenerationId,
    instagramNetwork,
    launchInstagramOAuthRedirectFlow,
    resetOAuthAttemptState,
    setIsConnecting,
    startInstagramOAuthPopupFlow,
    t,
  ]);

  useChatSocketInstagramOAuthReturn({
    enabled: shouldUseInstagramOAuthRedirect(),
    onSuccess: async (result) => {
      oauthHandledRef.current = true;
      clearInstagramOAuthPending();

      const connected = await handleInstagramOAuthSuccessRef.current?.(
        result?.businessId || businessId,
      );

      if (connected) {
        return;
      }

      oauthHandledRef.current = false;

      if (restartInstagramOAuthAttemptRef.current?.()) {
        return;
      }

      oauthHandledRef.current = true;
      oauthRetryCountRef.current = 0;
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
    },
    onFailure: () => {
      if (restartInstagramOAuthAttemptRef.current?.()) {
        return;
      }

      oauthHandledRef.current = true;
      oauthRetryCountRef.current = 0;
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
    },
  });

  useEffect(() => {
    if (shouldUseInstagramOAuthRedirect()) {
      return undefined;
    }

    const handleOAuthPopupMessage = async (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const messageData = event?.data;
      if (messageData?.type !== OAUTH_COMPLETED_MESSAGE_TYPE) {
        return;
      }

      if (messageData?.provider !== 'instagram') {
        return;
      }

      oauthPostMessageReceivedRef.current = true;
      oauthHasReachedRedirectRef.current = true;

      await completeInstagramOAuth({
        success: messageData?.success !== false,
        closePopup: false,
        oauthBusinessId: businessId,
      });
    };

    window.addEventListener('message', handleOAuthPopupMessage);

    return () => {
      window.removeEventListener('message', handleOAuthPopupMessage);
    };
  }, [businessId, completeInstagramOAuth]);

  useEffect(() => () => clearInstagramOAuthWatchers(), [clearInstagramOAuthWatchers]);

  return {
    startInstagramOAuth,
    clearInstagramOAuthWatchers,
    cancelInstagramOAuth,
    reopenInstagramOAuthWindow,
    usesInstagramOAuthRedirect: shouldUseInstagramOAuthRedirect(),
  };
};
